/**
 * Book Generator Component for Admin Panel
 * Generate professional study books for Amazon KDP
 */
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  BookOpen, Download, Loader2, Check, AlertTriangle, 
  FileText, Sparkles, RefreshCw, Trash2, Eye, Clock,
  BookMarked, FileCheck, ChevronDown, ChevronUp
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem('session_token');

const BookGenerator = ({ professions = [] }) => {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedProfession, setSelectedProfession] = useState('');
  const [outputFormat, setOutputFormat] = useState('both');
  const [generationJobs, setGenerationJobs] = useState({});
  const [expandedBook, setExpandedBook] = useState(null);

  useEffect(() => {
    loadBooks();
  }, []);

  useEffect(() => {
    // Poll for job status updates
    const runningJobs = Object.values(generationJobs).filter(
      job => job.status === 'queued' || job.status === 'generating'
    );

    if (runningJobs.length > 0) {
      const interval = setInterval(() => {
        runningJobs.forEach(job => checkJobStatus(job.job_id));
      }, 3000);

      return () => clearInterval(interval);
    }
  }, [generationJobs]);

  const loadBooks = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/books/list`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setBooks(response.data.books || []);
    } catch (error) {
      toast.error('Error al cargar libros');
    } finally {
      setLoading(false);
    }
  };

  const generateBook = async () => {
    if (!selectedProfession) {
      toast.error('Selecciona una profesión');
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/books/generate`,
        {
          profession_id: selectedProfession,
          output_format: outputFormat,
          include_clinical_cases: true,
          include_practice_qr: true
        },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      const jobId = response.data.job_id;
      
      setGenerationJobs(prev => ({
        ...prev,
        [jobId]: {
          job_id: jobId,
          profession_name: response.data.profession,
          status: 'queued',
          progress: 0,
          message: 'En cola...'
        }
      }));

      toast.success('Generación de libro iniciada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar generación');
    } finally {
      setGenerating(false);
    }
  };

  const checkJobStatus = async (jobId) => {
    try {
      const response = await axios.get(
        `${API}/books/generate/status/${jobId}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      setGenerationJobs(prev => ({
        ...prev,
        [jobId]: response.data
      }));

      if (response.data.status === 'completed') {
        toast.success(`Libro "${response.data.result?.title}" generado correctamente`);
        loadBooks();
      } else if (response.data.status === 'failed') {
        toast.error(`Error: ${response.data.message}`);
      }
    } catch (error) {
      console.error('Error checking job status:', error);
    }
  };

  const downloadBook = async (bookId, format) => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(
        `${API}/books/download/${bookId}?format=${format}`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `libro_${bookId}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Libro descargado en formato ${format.toUpperCase()}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al descargar libro');
    }
  };

  const triggerAIReview = async (bookId) => {
    try {
      await axios.post(
        `${API}/books/ai-review/${bookId}`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success('Revisión con IA iniciada');
    } catch (error) {
      toast.error('Error al iniciar revisión');
    }
  };

  const deleteBook = async (bookId) => {
    if (!window.confirm('¿Eliminar este libro?')) return;

    try {
      await axios.delete(`${API}/books/${bookId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success('Libro eliminado');
      loadBooks();
    } catch (error) {
      toast.error('Error al eliminar libro');
    }
  };

  const getKDPChecklist = async (bookId) => {
    try {
      const response = await axios.get(
        `${API}/books/${bookId}/kdp-checklist`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      return response.data;
    } catch (error) {
      toast.error('Error al obtener checklist');
      return null;
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      generated: { color: 'bg-blue-100 text-blue-700', label: 'Generado' },
      verified: { color: 'bg-emerald-100 text-emerald-700', label: 'Verificado' },
      queued: { color: 'bg-amber-100 text-amber-700', label: 'En cola' },
      generating: { color: 'bg-purple-100 text-purple-700', label: 'Generando' },
      failed: { color: 'bg-red-100 text-red-700', label: 'Error' }
    };
    const badge = badges[status] || { color: 'bg-slate-100 text-slate-700', label: status };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <BookMarked className="w-6 h-6 text-indigo-600" />
            Generador de Libros para Amazon KDP
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Genera libros de preparación profesionales listos para vender en Amazon
          </p>
        </div>
        <Button onClick={loadBooks} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Generation Form */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-6">
        <h3 className="font-semibold text-indigo-900 mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5" />
          Generar Nuevo Libro
        </h3>

        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Profesión / Oposición
            </label>
            <select
              value={selectedProfession}
              onChange={(e) => setSelectedProfession(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Seleccionar profesión...</option>
              {professions.filter(p => p.is_active).map(prof => (
                <option key={prof.profession_id} value={prof.profession_id}>
                  {prof.name} - {prof.region || 'Nacional'}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Formato de Salida
            </label>
            <select
              value={outputFormat}
              onChange={(e) => setOutputFormat(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              <option value="both">PDF + ePub (Recomendado)</option>
              <option value="pdf">Solo PDF (Impresión)</option>
              <option value="epub">Solo ePub (Kindle)</option>
            </select>
          </div>

          <div className="flex items-end">
            <Button
              onClick={generateBook}
              disabled={generating || !selectedProfession}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Iniciando...
                </>
              ) : (
                <>
                  <BookOpen className="w-4 h-4 mr-2" />
                  Generar Libro
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Book structure info */}
        <div className="mt-4 p-4 bg-white/60 rounded-lg">
          <p className="text-sm font-medium text-slate-700 mb-2">El libro incluirá:</p>
          <div className="grid md:grid-cols-2 gap-2 text-sm text-slate-600">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-emerald-500" />
              8 secciones por capítulo
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-emerald-500" />
              QR codes a la app
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-emerald-500" />
              Formato A5 para KDP
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-emerald-500" />
              Bibliografía completa
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-emerald-500" />
              Casos clínicos (sanidad)
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-emerald-500" />
              Cuaderno de trabajo
            </div>
          </div>
        </div>
      </div>

      {/* Active Jobs */}
      {Object.keys(generationJobs).length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <h3 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Generaciones en Progreso
          </h3>
          <div className="space-y-3">
            {Object.values(generationJobs).map(job => (
              <div key={job.job_id} className="bg-white rounded-lg p-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {job.status === 'completed' ? (
                    <Check className="w-5 h-5 text-emerald-500" />
                  ) : job.status === 'failed' ? (
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                  ) : (
                    <Loader2 className="w-5 h-5 text-indigo-500 animate-spin" />
                  )}
                  <div>
                    <p className="font-medium text-slate-900">{job.profession_name}</p>
                    <p className="text-sm text-slate-500">{job.message}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {job.progress > 0 && (
                    <div className="w-32 h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-indigo-500 transition-all duration-300"
                        style={{ width: `${job.progress}%` }}
                      />
                    </div>
                  )}
                  {getStatusBadge(job.status)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Books List */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900">Libros Generados ({books.length})</h3>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="w-8 h-8 text-indigo-500 animate-spin mx-auto mb-2" />
            <p className="text-slate-500">Cargando libros...</p>
          </div>
        ) : books.length === 0 ? (
          <div className="p-12 text-center">
            <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No hay libros generados todavía</p>
            <p className="text-sm text-slate-400">Selecciona una profesión y genera tu primer libro</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-200">
            {books.map(book => (
              <div key={book.book_id} className="p-4 hover:bg-slate-50">
                <div 
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setExpandedBook(expandedBook === book.book_id ? null : book.book_id)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <BookMarked className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">{book.title}</h4>
                      <p className="text-sm text-slate-500">
                        {book.subtitle} • {book.region} • {book.chapters_count} capítulos
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(book.status)}
                    {book.ai_reviewed && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                        IA Revisado
                      </span>
                    )}
                    {expandedBook === book.book_id ? (
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                </div>

                {/* Expanded details */}
                {expandedBook === book.book_id && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-slate-500 mb-1">Creado</p>
                        <p className="font-medium">
                          {new Date(book.created_at).toLocaleString('es-ES')}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500 mb-1">Formatos disponibles</p>
                        <p className="font-medium">
                          {Object.keys(book.files || {}).map(f => f.toUpperCase()).join(', ') || 'N/A'}
                        </p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2">
                      {book.files?.pdf && (
                        <Button
                          size="sm"
                          onClick={() => downloadBook(book.book_id, 'pdf')}
                          className="bg-red-600 hover:bg-red-700 text-white"
                        >
                          <Download className="w-4 h-4 mr-1" />
                          PDF
                        </Button>
                      )}
                      {book.files?.epub && (
                        <Button
                          size="sm"
                          onClick={() => downloadBook(book.book_id, 'epub')}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          <Download className="w-4 h-4 mr-1" />
                          ePub
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => triggerAIReview(book.book_id)}
                        disabled={book.ai_reviewed}
                      >
                        <Sparkles className="w-4 h-4 mr-1" />
                        Revisar con IA
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={async () => {
                          const checklist = await getKDPChecklist(book.book_id);
                          if (checklist) {
                            toast.success(
                              checklist.ready_for_kdp 
                                ? '✅ Libro listo para Amazon KDP'
                                : '⚠️ Revisa los requisitos de KDP'
                            );
                          }
                        }}
                      >
                        <FileCheck className="w-4 h-4 mr-1" />
                        Checklist KDP
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => deleteBook(book.book_id)}
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Eliminar
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* KDP Instructions */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
        <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-slate-600" />
          Cómo publicar en Amazon KDP
        </h3>
        <ol className="space-y-2 text-sm text-slate-600">
          <li className="flex items-start gap-2">
            <span className="w-6 h-6 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
            <span>Descarga el PDF del libro generado</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-6 h-6 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
            <span>Ve a <a href="https://kdp.amazon.com" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">kdp.amazon.com</a> y crea una cuenta</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-6 h-6 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
            <span>Crea un nuevo título y sube el PDF como interior del libro</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-6 h-6 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">4</span>
            <span>Usa Amazon Cover Creator o sube tu propia portada</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-6 h-6 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">5</span>
            <span>Configura precio y distribución, luego publica</span>
          </li>
        </ol>
      </div>
    </div>
  );
};

export default BookGenerator;
